import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AuthService,
  CountryService,
  LocalStorageService,
} from '@univacity/angular/datas';
import {
  ORGANISATION_CATEGORY,
  RoutePath,
  Setting,
} from '@univacity/shared/types';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFacebookSquare, faGoogle } from '@fortawesome/free-brands-svg-icons';
import {
  AngularUiModule,
  CustomValidators,
  DestroyAbleComponent,
  FormUtil,
} from '@univacity/angular/ui';
import { ApiError } from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'un-registration-form',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    AngularUiModule,
    NzGridModule,
    FontAwesomeModule,
    RecaptchaModule,
  ],
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class RegistrationFormComponent
  extends DestroyAbleComponent
  implements OnInit
{
  @Input() returnUrl?: string;
  @Input() showOrganisation?: boolean;

  form: FormGroup;
  loginLink?: string | string[] = [];
  refCode = '';
  faGoogle = faGoogle;
  faFacebook = faFacebookSquare;
  termsLink = RoutePath.terms;
  policyLink = RoutePath.policy;
  types = ObjectUtil.enumToKeyLabelValuePair(ORGANISATION_CATEGORY);
  category?: ORGANISATION_CATEGORY;
  recaptchaKey = environment.recaptchaKey;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private ch: ChangeDetectorRef,
    private message: NzMessageService,
    public countrySvc: CountryService,
    private localStorage: LocalStorageService,
    @Optional() public modalRef?: NzModalRef,
    @Optional()
    @Inject(NZ_MODAL_DATA)
    private nzModalData?: {
      returnUrl?: string;
      showOrganisation?: boolean;
    }
  ) {
    super();
  }

  ngOnInit(): void {
    this.returnUrl ??= this.nzModalData?.returnUrl;
    this.showOrganisation ??= this.nzModalData?.showOrganisation;

    if (this.auth.isLoggedIn) {
      this.router.navigate([RoutePath.dashboard]);
      return;
    }
    if (this.localStorage.getItem('refCode'))
      this.refCode = this.localStorage.getItem(Setting.refCode) as string;
    else {
      if (this.router.url.includes('/r/'))
        this.refCode = this.router.url.replace('/r/', '');
    }
    this.returnUrl =
      this.returnUrl || this.route.snapshot.queryParams['returnUrl'];
    this.initLinks();

    this.initForm();
  }

  private initForm() {
    const { type } = this.route.snapshot.queryParams;
    if (Object.values(ORGANISATION_CATEGORY).includes(type))
      this.category = type;
    this.form = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      password: ['', [Validators.required]],
      phone: ['', this.showOrganisation ? [] : [Validators.required]],
      organisationName: [
        '',
        this.showOrganisation ? [Validators.required] : [],
      ],
      organisationCategory: [
        this.category,
        this.showOrganisation ? [Validators.required] : [],
      ],
      confirmPassword: ['', [Validators.required]],
      agreeToTerms: [false, [Validators.requiredTrue]],
      temporary: [false],
      returnUrl: [this.returnUrl],
      referredByCode: [this.refCode],
      recaptcha: ['', [Validators.required]],
    });
    this.form.addValidators(
      CustomValidators.mustMatch('confirmPassword', 'password')
    );
  }

  initLinks() {
    if (!this.modalRef) this.loginLink = RoutePath.authLogin;
  }

  register() {
    if (
      !this.form.value.referredByCode &&
      this.localStorage.getItem(Setting.refCode)
    )
      this.form.patchValue({
        referredByCode: this.localStorage.getItem(Setting.refCode),
      });
    FormUtil.updateFormValidity(this.form);
    if (this.form.invalid) {
      if (this.form.get('agreeToTerms')?.invalid)
        this.message.error($localize`You have to agree to terms`);
      else if (this.form.get('recaptcha')?.invalid)
        this.message.error($localize`Solve Recaptca`);
      else this.message.error($localize`Invaild form`);
      return;
    }
    this.auth
      .signUp(this.form.value, this.form.value.recaptcha)
      .pipe(takeUntil(this.dead$))

      .subscribe({
        next: (u) => {
          if (this.modalRef) this.modalRef?.close();
          else if (u.organisation) {
            this.router.navigate([
              RoutePath.dasshboardCompliance,
              u.organisation,
            ]);
          } else {
            this.router.navigate([this.returnUrl || RoutePath.dashboard]);
          }
        },
        error: (err?: ApiError) =>
          err?.message && this.message.error(err.message),
      });

    this.ch.detectChanges();
  }
  oauth(provider: 'facebook' | 'google' | 'keycloak') {
    window.location.href = `${
      environment.mainServiceEndpoint
    }/auth/${provider}?redirect=${
      this.returnUrl
        ? window.location.origin + this.returnUrl
        : window.location.href
    }`;
  }

  resolveRecaptcha(recaptcha?: string | null) {
    this.form.patchValue({ recaptcha });
  }
}
